import { useEffect, useState } from "react";
import Select from "react-select";
import stylez from "./createOrder.module.css";
import SuccessDialogue from "../../../Components/Shared/SuccessDialog/SuccessDialog";
import axiosInstance from "../../../Configuration/axiosInstance";
import { FaTrash } from "react-icons/fa";
import LoadingSpinner from "../../../Components/Shared/Loader/Loader";

const CreateOrder = () => {
  const [customers, setCustomers] = useState();
  const [loading, setLoading] = useState(true);
  const [custId, setCustID] = useState();
  const [prodID, setProdId] = useState({});
  const [products, setProducts] = useState();
  const [quantity, setQuantity] = useState("");
  const [date, setDate] = useState();
  const [productArr, setProductArr] = useState([]);
  const [msg, setMsg] = useState("");
  const [scheduled, setScheduled] = useState(false);
  // ✅ NEW: State for pickup/delivery
  const [isPickup, setIsPickup] = useState(false);
  const [comment, setComment] = useState("");
  const [dialogBox, setDialogBox] = useState(false);
  const [isError, setIsError] = useState(false);
  const [stockValidationResults, setStockValidationResults] = useState(null);

  //Purchase order number
  const [poc, setPoc] = useState();
  let price;
  let totalPrice;

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [customersResponse, productsResponse] = await Promise.all([
          axiosInstance.get(`customers/allcustomers`),
          axiosInstance.get(`products/allproducts`),
        ]);
        setCustomers(customersResponse.data.data);
        setProducts(productsResponse.data.data);
        setLoading(false);
      } catch (err) {
        alert(err);
        setLoading(false);
      }
    };
    fetchAllData();
  }, []);

  const customerOptions = customers?.map((i) => ({
    value: i._id,
    label: i.businessName,
  }));
  const productOptions = products?.map((i) => ({
    value: i._id,
    label: `${i.productName}-${i.sku}`,
  }));

  const customerHandler = (options) => {
    setCustID(options.value);
  };

  const productHandler = (options) => {
    const { value } = options;
    setProdId({ label: value });
  };

  const resetForm = () => {
    setDate("");
    setCustID(null);
    setProdId({});
    setQuantity("");
    setProductArr([]);
    setScheduled(false);
    // ✅ NEW: Reset pickup state
    setIsPickup(false);
    setComment("");
    setStockValidationResults(null);
    setIsError(false);
  };

  // ✅ ENHANCED: Show detailed error information
  const showError = (errorMessage, isStockError = false) => {
    setIsError(true);
    setMsg(errorMessage);
    setDialogBox(true);

    // ✅ Longer timeout for stock errors since they contain more information
    const timeout = isStockError ? 8000 : 3700;
    setTimeout(() => {
      setDialogBox(false);
      setIsError(false);
    }, timeout);
  };

  // ✅ ENHANCED: Show success with stock validation details
  const showSuccess = (successMessage, stockValidation = null) => {
    setIsError(false);
    setMsg(successMessage);
    setStockValidationResults(stockValidation);
    setDialogBox(true);

    setTimeout(() => {
      resetForm();
      setDialogBox(false);
      setStockValidationResults(null);
    }, 5000);
  };

  const submitOrder = async () => {
    // Check if all required fields are initialized
    if (!date || !custId || productArr.length === 0) {
      showError("Please fill in all required fields ❗");
      return;
    }

    try {
      setLoading(true);
      const res = await axiosInstance.post(`order`, {
        customer: custId,
        date,
        products: [...productArr],
        scheduled,
        // ✅ NEW: Include pickup flag
        isPickup,
        purchaseOrdeNo: poc,
        comments: comment,
      });

      setLoading(false);

      // ✅ ENHANCED: Show success with order type info
      const customerName =
        customers.find((c) => c._id === custId)?.businessName || "Customer";
      const orderType = isPickup ? "Pickup" : "Delivery";
      const successMessage = `${customerName} - ${orderType} ${res.data.message}`;

      showSuccess(successMessage, res.data.data?.stockValidation);
    } catch (error) {
      setLoading(false);
      console.error("Order creation error:", error);

      if (error.response && error.response.status >= 400 && error.response.status <= 500) {
        // ✅ ENHANCED: Handle different types of errors with detailed messages
        const errorData = error.response.data;
        let errorMessage = errorData.error || errorData.message || "An error occurred";

        // ✅ NEW: Check if it's a stock-related error and format it nicely
        if (
          errorMessage.includes("Insufficient available stock") ||
          errorMessage.includes("Stock not found") ||
          errorMessage.includes("warehouse not found") ||
          errorMessage.includes("No stock entry found")
        ) {
          // ✅ Format stock errors for better readability
          errorMessage = formatStockError(errorMessage);
          showError(errorMessage, true); // true indicates it's a stock error
        } else {
          showError(errorMessage);
        }
      } else {
        showError("Network error. Please check your connection and try again.");
      }
    }
  };

  // ✅ NEW: Format stock error messages for better readability
  const formatStockError = (errorMessage) => {
    // If it's a detailed stock error, format it nicely
    if (errorMessage.includes("Current stock:") && errorMessage.includes("Already reserved:")) {
      const lines = errorMessage.split(". ");
      return lines.join(".\n\n");
    }

    // For other stock errors, return as is
    return errorMessage;
  };

  const removeProduct = (id) => {
    setProductArr(productArr.filter((i) => i.product !== id));
  };

  const handleDate = (e) => {
    const today = new Date();
    const yyyy = today.getFullYear();
    let mm = today.getMonth() + 1;
    let dd = today.getDate();

    if (dd < 10) dd = "0" + dd;
    if (mm < 10) mm = "0" + mm;

    const formattedToday = mm + "/" + dd + "/" + yyyy;
    if (e.target.value < formattedToday) {
      e.target.value = formattedToday;
      alert("You cannot select a past date");
    }
    setDate(e.target.value);
  };

  const handlePriceChange = (productId, newPrice) => {
    const updatedPrice = newPrice === "" ? "" : Number(newPrice);

    setProductArr((prevProductArr) =>
      prevProductArr.map((product) =>
        product.product === productId
          ? {
              ...product,
              price: updatedPrice,
              total:
                product.quantity === ""
                  ? ""
                  : (Number(product.quantity) * updatedPrice).toFixed(2),
            }
          : product
      )
    );
  };

  const handleQuantityChange = (id, newQuantity) => {
    const quantityValue = newQuantity === "" ? "" : Number(newQuantity);

    setProductArr((prevProductArr) =>
      prevProductArr.map((product) =>
        product.product === id
          ? {
              ...product,
              quantity: quantityValue,
              total:
                quantityValue === ""
                  ? ""
                  : (quantityValue * product.price).toFixed(2),
            }
          : product
      )
    );
  };

  const addProducts = () => {
    if (prodID.length === 0) {
      return;
    } else {
      const prodId = prodID.label;
      const selectedProduct = products.find(
        (product) => product._id === prodId
      );

      price = Number(selectedProduct?.price);
      totalPrice = Number(quantity) * price;
      totalPrice = totalPrice.toFixed(2);

      if (productArr.find((i) => i.product === prodId)) {
        setProductArr((prevValue) =>
          prevValue.map((item) =>
            item.product === prodId
              ? {
                  ...item,
                  quantity:
                    quantity !== ""
                      ? Number(quantity) + Number(item.quantity)
                      : item.quantity,
                  total:
                    quantity !== ""
                      ? (Number(quantity) + Number(item.quantity)) * price
                      : item.total,
                }
              : item
          )
        );
      } else {
        setProductArr((prevValue) => [
          ...prevValue,
          { product: prodId, quantity, price, total: totalPrice },
        ]);
      }
    }
  };

  const handleCheckbox = (e) => {
    setScheduled(e.target.checked);
  };

  // ✅ NEW: Handle pickup checkbox
  const handlePickupCheckbox = (e) => {
    setIsPickup(e.target.checked);
  };

  const handleClose = () => {
    setDialogBox(false);
    setStockValidationResults(null);
    setIsError(false);
  };

  // ✅ NEW: Custom styles for react-select
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      padding: '0.25rem',
      borderRadius: '4px',
      border: state.isFocused ? '2px solid #007bff' : '1px solid #ced4da',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
      fontSize: '1rem',
      minHeight: '48px',
      '&:hover': {
        border: state.isFocused ? '2px solid #007bff' : '1px solid #adb5bd'
      }
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#6c757d',
      fontWeight: '400'
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#495057',
      fontWeight: '500'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? '#007bff' 
        : state.isFocused 
        ? '#f8f9fa' 
        : 'white',
      color: state.isSelected ? 'white' : '#495057',
      padding: '12px 16px',
      fontSize: '1rem',
      '&:hover': {
        backgroundColor: state.isSelected ? '#007bff' : '#e9ecef'
      }
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '4px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e9ecef'
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '200px',
      padding: '4px'
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: '#dee2e6'
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isFocused ? '#007bff' : '#6c757d',
      '&:hover': {
        color: '#007bff'
      }
    })
  };

  return (
    <div className={stylez.container}>
      <h2 className={stylez.title}>Create Sales Order</h2>

      {/* ✅ ENHANCED: Custom dialog for success/error with stock validation */}
      {dialogBox && (
        <div className={stylez.dialogOverlay}>
          <div
            className={`${stylez.dialogBox} ${
              isError ? stylez.errorDialog : stylez.successDialog
            }`}
          >
            <div className={stylez.dialogHeader}>
              <h3 className={stylez.dialogTitle}>
                {isError
                  ? "❌ Order Creation Failed"
                  : "✅ Order Created Successfully"}
              </h3>
              <button
                className={stylez.closeButton}
                onClick={handleClose}
              >
                ×
              </button>
            </div>

            <div className={stylez.dialogContent}>
              <div className={stylez.dialogMessage}>
                {msg.split("\n\n").map((line, index) => (
                  <p key={index} className={stylez.messageLine}>
                    {line}
                  </p>
                ))}
              </div>

              {/* ✅ NEW: Show stock validation results for successful orders */}
              {!isError && stockValidationResults && (
                <div className={stylez.stockValidationSummary}>
                  <h4>📊 Stock Validation Summary:</h4>
                  <div className={stylez.validationStats}>
                    <span>
                      ✅{" "}
                      {stockValidationResults.validationResults?.length || 0}{" "}
                      products validated
                    </span>
                    <span>
                      📦{" "}
                      {
                        stockValidationResults.reservationSummary?.totalPendingOrders ||
                        0
                      }{" "}
                      total pending orders
                    </span>
                  </div>

                  {stockValidationResults.validationResults?.length > 0 && (
                    <div className={stylez.validationDetails}>
                      <h5>Stock Reservations:</h5>
                      {stockValidationResults.validationResults.map((result, index) => (
                        <div key={index} className={stylez.validationItem}>
                          <strong>{result.productName}</strong>: {result.willBeReserved} units reserved
                          (Available: {result.availableStock})
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className={stylez.dialogFooter}>
              <button className={stylez.dialogCloseBtn} onClick={handleClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className={stylez.loaderContainer}>
          <LoadingSpinner />
        </div>
      ) : (
        <div className={stylez.orderForm}>
          <div className={stylez.orderBasicInfo}>
            <div className={stylez.formRow}>
              <div className={stylez.formGroup}>
                <label>Order Date:</label>
                <input 
                  required 
                  type="date" 
                  value={date} 
                  onChange={handleDate}
                  className={stylez.formInput}
                />
              </div>

              <div className={stylez.formGroup}>
                <label>Customer:</label>
                <Select
                  required
                  name="Customer"
                  options={customerOptions}
                  onChange={customerHandler}
                  placeholder="Select customer..."
                  styles={selectStyles} // ✅ Apply custom styles
                  isSearchable={true}
                  isClearable={true}
                  className={stylez.reactSelect}
                  classNamePrefix="react-select"
                />
              </div>

              <div className={stylez.formGroup}>
                <label>Purchase Order Number:</label>
                <input
                  type="text"
                  name="poc"
                  placeholder="Enter PO number (optional)"
                  onChange={(e) => setPoc(e.target.value)}
                  className={stylez.formInput}
                />
              </div>
            </div>

            {/* ✅ NEW: Order Type Section */}
            <div className={stylez.orderTypeSection}>
              <h3 className={stylez.sectionTitle}>Order Type & Options</h3>
              <div className={stylez.checkboxRow}>
                <div className={stylez.checkboxGroup}>
                  <label className={stylez.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={isPickup}
                      onChange={handlePickupCheckbox}
                      className={stylez.checkbox}
                    />
                    <span className={stylez.checkboxText}>
                      Pickup
                    </span>
                  </label>
                </div>

                <div className={stylez.checkboxGroup}>
                  <label className={stylez.checkboxLabel}>
                    <input
                      type="checkbox"
                      onChange={handleCheckbox}
                      checked={scheduled}
                      className={stylez.checkbox}
                    />
                    <span className={stylez.checkboxText}>
                      Scheduled Order
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className={stylez.formGroup}>
              <label>Order Comments:</label>
              <textarea
                rows="4"
                placeholder="Comments about this order..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className={stylez.textarea}
              />
            </div>
          </div>

          <div className={stylez.productContainer}>
            <label>Product: </label>
            <Select
              required
              name="product"
              options={productOptions}
              onChange={productHandler}
              placeholder="Select product..."
              styles={selectStyles} // ✅ Apply same styles to product select
              isSearchable={true}
              isClearable={true}
              className={stylez.reactSelect}
              classNamePrefix="react-select"
            />
            <button className={stylez.addProductBtn} onClick={addProducts}>
              Add Products +
            </button>
          </div>

          <div className={stylez.productsView}>
            <table>
              <thead className={stylez.headers}>
                <tr>
                  <th>Product</th>
                  <th>Quantity</th>
                  <th>Price</th>
                  <th>Price(RRP)</th>
                  <th>Total</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {productArr?.map((i, index) => {
                  const id = i.product;
                  const prod = products.filter((p) => p._id === id);

                  return (
                    <tr className={stylez.products} key={index}>
                      <td>{prod.map((p) => p.productName)}</td>
                      <td>
                        <input
                          type="number"
                          value={i.quantity}
                          onChange={(e) => handleQuantityChange(id, e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          onChange={(e) => handlePriceChange(id, e.target.value)}
                          defaultValue={i.price}
                        />
                      </td>
                      <td>{prod.map((p) => p.price)}</td>
                      <td>{i.total}</td>
                      <td>
                        <button className={stylez.icon} onClick={() => removeProduct(id)}>
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ✅ ENHANCED: Submit button with order type indication */}
          <button className="confirmBtn" onClick={submitOrder}>
            Create {isPickup ? "Pickup" : "Delivery"} Order
          </button>
        </div>
      )}
    </div>
  );
};

export default CreateOrder;
